import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PreVisitPhoto, PreVisitQuestion } from '@/api/preVisit/preVisitApi.types';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  ALLOWED_PRE_VISIT_PHOTO_CONTENT_TYPES,
  PRE_VISIT_PHOTOS_PER_QUESTION_MAX,
} from '@/constants/preVisitQuestionTypes';
import { buildAcceptAttribute, isAcceptedContentType } from '@/constants/media';
import { showErrorNotification } from '@/utils/notifications';
import { PreVisitQuestionLabel } from './PreVisitQuestionLabel';

const PRE_VISIT_PHOTO_INPUT_ACCEPT = buildAcceptAttribute(ALLOWED_PRE_VISIT_PHOTO_CONTENT_TYPES);

function isPreviewableContentType(contentType: string): boolean {
  return contentType !== 'image/heic' && contentType !== 'image/heif';
}

interface PhotosRendererProps {
  question: PreVisitQuestion;
  photos: PreVisitPhoto[];
  isInvalid: boolean;
  isUploading: boolean;
  isDisabled: boolean;
  onUpload: (file: File) => void;
  onRemove: (photo: PreVisitPhoto) => void;
  onPreview: (photo: PreVisitPhoto) => void;
}

export function PhotosRenderer({
  question,
  photos,
  isInvalid,
  isUploading,
  isDisabled,
  onUpload,
  onRemove,
  onPreview,
}: PhotosRendererProps) {
  const { t } = useTranslation('intake');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [photoPendingRemoval, setPhotoPendingRemoval] = useState<PreVisitPhoto | null>(null);
  const hasReachedLimit = photos.length >= PRE_VISIT_PHOTOS_PER_QUESTION_MAX;

  function selectFile() {
    if (fileInputRef.current !== null) {
      fileInputRef.current.click();
    }
  }

  function handleFileSelected(changeEvent: React.ChangeEvent<HTMLInputElement>) {
    const selectedFiles = changeEvent.target.files;
    const selected = selectedFiles === null ? null : selectedFiles.item(0);
    changeEvent.target.value = '';
    if (selected === null) {
      return;
    }
    if (isAcceptedContentType(selected, ALLOWED_PRE_VISIT_PHOTO_CONTENT_TYPES) === false) {
      showErrorNotification(t('preVisit.photos.unsupportedType'));
      return;
    }
    onUpload(selected);
  }

  return (
    <div className="space-y-1.5">
      <PreVisitQuestionLabel question={question} />

      <div
        className={
          'p-3 rounded-xl ' +
          (isInvalid ? 'border border-zooerrorborder ring-1 ring-zooerrorborder' : 'border border-transparent')
        }
      >
        <div className="flex items-center justify-between gap-2 mb-3">
          <Button
            type="button"
            variant="outline"
            size="chip"
            isLoading={isUploading}
            disabled={isDisabled || hasReachedLimit}
            onClick={selectFile}
          >
            <Icon name="Upload" />
            {t('preVisit.photos.addPhoto')}
          </Button>
          <span className="text-xs font-semibold text-zoomuted">
            {t('preVisit.photos.count', {
              current: photos.length,
              max: PRE_VISIT_PHOTOS_PER_QUESTION_MAX,
            })}
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept={PRE_VISIT_PHOTO_INPUT_ACCEPT}
            className="hidden"
            onChange={handleFileSelected}
          />
        </div>

        {photos.length === 0 ? (
          <p className="text-sm text-zoomuted">{t('preVisit.photos.empty')}</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {photos.map(function renderPhoto(photo) {
              return (
                <div key={photo.id} className="relative aspect-square">
                  <button
                    type="button"
                    onClick={function preview() {
                      onPreview(photo);
                    }}
                    className="w-full h-full rounded-xl overflow-hidden border border-zooborder bg-zoooffwhite flex items-center justify-center"
                  >
                    {isPreviewableContentType(photo.contentType) ? (
                      <img
                        src={photo.url}
                        alt={photo.filename}
                        width={200}
                        height={200}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="flex flex-col items-center gap-1 text-zoomuted px-1">
                        <Icon name="FileText" />
                        <span className="text-[10px] truncate max-w-full">{photo.filename}</span>
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    aria-label={t('preVisit.photos.remove')}
                    disabled={isDisabled}
                    onClick={function requestRemoval() {
                      setPhotoPendingRemoval(photo);
                    }}
                    className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-zooindigo text-zoowhite flex items-center justify-center shadow-md"
                  >
                    <Icon name="X" className="text-xs" />
                  </button>
                </div>
              );
            })}
            {isUploading ? (
              <div className="aspect-square rounded-xl border border-dashed border-zooborder flex items-center justify-center">
                <LoadingSpinner className="w-5 h-5" />
              </div>
            ) : null}
          </div>
        )}
      </div>

      {photoPendingRemoval === null ? null : (
        <ConfirmDialog
          tone="danger"
          iconName="Trash"
          title={t('preVisit.photos.removeConfirmTitle')}
          description={t('preVisit.photos.removeConfirmBody')}
          confirmLabel={t('preVisit.photos.remove')}
          onConfirm={function confirmRemoval() {
            onRemove(photoPendingRemoval);
            setPhotoPendingRemoval(null);
          }}
          onCancel={function cancelRemoval() {
            setPhotoPendingRemoval(null);
          }}
        />
      )}
    </div>
  );
}
